// Eskom PARCC — QA notes on working papers (Lindiwe feedback Jun 23 2026, 4a).
//
// `useQaNotes` reads every (non-deleted) QA note; the page groups them by
// referenceNo. Writes go THROUGH admin-api (adminApi.notifyQaNote / resolveQaNote)
// so the author is forge-proof and the reviewer alert fires in the same call — the
// hooks just invalidate the QA-note cache and surface a toast.
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { queryKeys } from '../lib/queryKeys';
import { fetchQaNotes, type QaNoteRead } from '../lib/db';
import { adminApi } from '../lib/adminApi';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../lib/auth';

/** All QA notes the user can see (grouped by referenceNo in the page). */
export function useQaNotes() {
  const { role } = useAuth();
  return useQuery<QaNoteRead[], Error>({
    queryKey: queryKeys.qaNotes,
    queryFn: fetchQaNotes,
    enabled: !!role,
  });
}

interface AddQaNoteArgs {
  referenceNo: string;
  note: string;
}

/** Add a QA note to a working paper (manager/admin; server-enforced + alerts). */
export function useAddQaNote() {
  const { show } = useToast();
  return useMutation({
    mutationFn: ({ referenceNo, note }: AddQaNoteArgs) => adminApi.notifyQaNote(referenceNo, note),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.qaNotes });
      show(res.alerted ? 'QA note added — the reviewer has been notified.' : 'QA note added.', 'success');
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to add the QA note.', 'error');
    },
  });
}

interface ResolveQaNoteArgs {
  id: string;
  resolved: boolean;
}

/** Resolve / re-open a QA note (manager/admin; server-enforced). */
export function useResolveQaNote() {
  const { show } = useToast();
  return useMutation({
    mutationFn: ({ id, resolved }: ResolveQaNoteArgs) => adminApi.resolveQaNote(id, resolved),
    onSuccess: (_res, { resolved }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.qaNotes });
      show(resolved ? 'QA note resolved.' : 'QA note re-opened.', 'success');
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to update the QA note.', 'error');
    },
  });
}
